/**
 * @voltxampere/persistence — `.vxa` native format codec (Sprint 21)
 *
 * Sprint 21 ships .vxa as a **gzip-compressed UTF-8 JSON document**
 * (D-21-4 closure: Şefim approved native CompressionStream over
 * jszip ZIP archive — +0 KB bundle cost). The Sprint 22+ asset
 * support upgrade may move to a multi-file ZIP layout (forward-
 * compat magic byte reserved in the manifest's `vxaVersion`).
 *
 * On-disk shape:
 *
 *   gzip(JSON.stringify({
 *     vxaVersion: "1.0.0",
 *     manifest: { schemaVersion, createdAt, updatedAt, generator },
 *     circuit:  { ...Circuit }
 *   }))
 *
 * Migration on import: circuit's `schemaVersion` is run through
 * `migrateToLatest` (current 0.4.0). Failures surface as a typed
 * VxaFormatError; the AYARLAR import flow shows a toast and skips
 * the IndexedDB write.
 *
 * Browser support: native CompressionStream / DecompressionStream
 * with `"gzip"` algorithm requires Chrome 80+ / Firefox 113+ /
 * Safari 16.4+. Sprint 21 baseline assumes these versions.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { migrateToLatest } from "@voltxampere/schema";
export const VXA_FORMAT_VERSION = "1.0.0";
export const VXA_GENERATOR = "VoltXAmpere v3 · Sprint 21";
export const VXA_MIME_TYPE = "application/x-vxa+gzip";
export class VxaFormatError extends Error {
    name = "VxaFormatError";
    cause;
    constructor(message, cause) {
        super(message);
        if (cause !== undefined)
            this.cause = cause;
    }
}
/**
 * Serialise a Circuit into a `.vxa` Blob. Result is gzip-encoded
 * UTF-8 JSON; recipient calls `fromVXA(blob)` to round-trip.
 */
export async function toVXA(circuit, opts = {}) {
    const now = opts.now ?? Date.now();
    const isoNow = new Date(now).toISOString();
    const manifest = {
        vxaVersion: VXA_FORMAT_VERSION,
        schemaVersion: circuit.schemaVersion,
        createdAt: isoNow,
        updatedAt: isoNow,
        generator: opts.generator ?? VXA_GENERATOR,
    };
    const payload = {
        vxaVersion: VXA_FORMAT_VERSION,
        manifest,
        circuit,
    };
    const json = JSON.stringify(payload);
    const compressed = await gzipString(json);
    return new Blob([compressed], { type: VXA_MIME_TYPE });
}
/**
 * Deserialise a `.vxa` Blob back into a `Circuit`. Runs schema
 * migration so older .vxa archives keep loading on newer engine
 * builds. Throws `VxaFormatError` on malformed input.
 */
export async function fromVXA(blob) {
    let text;
    try {
        text = await ungzipBlob(blob);
    }
    catch (err) {
        throw new VxaFormatError("fromVXA: failed to decompress blob — not a valid .vxa file", err);
    }
    let payload;
    try {
        payload = JSON.parse(text);
    }
    catch (err) {
        throw new VxaFormatError("fromVXA: payload is not valid JSON", err);
    }
    if (!isVxaPayload(payload)) {
        throw new VxaFormatError("fromVXA: payload missing required fields");
    }
    try {
        return migrateToLatest(payload.circuit);
    }
    catch (err) {
        throw new VxaFormatError(`fromVXA: schema migration failed (source ${payload.manifest.schemaVersion})`, err);
    }
}
/**
 * Read just the manifest header from a `.vxa` blob without
 * materialising the full circuit. Useful for import dialogs
 * that show "this file was generated by … on …" before the user
 * commits to opening it.
 */
export async function readVXAManifest(blob) {
    const text = await ungzipBlob(blob);
    const payload = JSON.parse(text);
    if (!isVxaPayload(payload)) {
        throw new VxaFormatError("readVXAManifest: payload missing manifest fields");
    }
    return payload.manifest;
}
// ────────────────────────────────────────────────────────────────
// Internal helpers — native gzip via CompressionStream
// ────────────────────────────────────────────────────────────────
async function gzipString(input) {
    if (typeof CompressionStream === "undefined") {
        throw new VxaFormatError("gzipString: CompressionStream API not available");
    }
    const encoder = new TextEncoder();
    const inputBytes = encoder.encode(input);
    const stream = new Blob([inputBytes]).stream().pipeThrough(new CompressionStream("gzip"));
    return await new Response(stream).arrayBuffer();
}
async function ungzipBlob(blob) {
    if (typeof DecompressionStream === "undefined") {
        throw new VxaFormatError("ungzipBlob: DecompressionStream API not available");
    }
    const stream = blob.stream().pipeThrough(new DecompressionStream("gzip"));
    return await new Response(stream).text();
}
function isVxaPayload(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    if (typeof v.vxaVersion !== "string")
        return false;
    const m = v.manifest;
    if (typeof m !== "object" || m === null)
        return false;
    const mr = m;
    if (typeof mr.vxaVersion !== "string")
        return false;
    if (typeof mr.schemaVersion !== "string")
        return false;
    if (typeof mr.createdAt !== "string")
        return false;
    if (typeof mr.updatedAt !== "string")
        return false;
    if (typeof mr.generator !== "string")
        return false;
    if (typeof v.circuit !== "object" || v.circuit === null)
        return false;
    return true;
}
//# sourceMappingURL=vxa-format.js.map